// web/src/hooks/use-ws.ts
import { useEffect, useRef, useState, useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { getAccessToken } from '@/lib/api'
import type { WebhookEvent } from '@/lib/types'

export function useWebSocket() {
  const [connected, setConnected] = useState(false)
  const [lastEvent, setLastEvent] = useState<WebhookEvent | null>(null)
  const wsRef = useRef<WebSocket | null>(null)
  const queryClient = useQueryClient()
  const reconnectDelay = useRef(1000)

  const connect = useCallback(() => {
    const token = getAccessToken()
    if (!token) return

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const ws = new WebSocket(`${protocol}//${window.location.host}/ws?token=${token}`)

    ws.onopen = () => {
      setConnected(true)
      reconnectDelay.current = 1000
    }

    ws.onmessage = (e) => {
      try {
        const event: WebhookEvent = JSON.parse(e.data)
        setLastEvent(event)
        // Prepend to cached events list
        queryClient.setQueryData<WebhookEvent[]>(['events'], (old) =>
          old ? [event, ...old].slice(0, 200) : [event]
        )
      } catch { /* ignore parse errors */ }
    }

    ws.onclose = () => {
      setConnected(false)
      // Reconnect with backoff
      setTimeout(() => {
        reconnectDelay.current = Math.min(reconnectDelay.current * 2, 30000)
        connect()
      }, reconnectDelay.current)
    }

    ws.onerror = () => ws.close()
    wsRef.current = ws
  }, [queryClient])

  useEffect(() => {
    connect()
    return () => wsRef.current?.close()
  }, [connect])

  return { connected, lastEvent }
}
